import { TFunction } from 'i18next';

import { ProjectBadgeSyntheticStatus } from '@/modules/common/components/molecules/badge/status-configs/project-status-config';
import { ProjectStatus } from '@/modules/projects/types/project';
import { formatDisplayDate } from '@/utils/date-formatters';

import { AssignmentProject } from '../types/assignment-project';

/** Props for a project's StatusBadge derived from its status info. */
export interface ProjectStatusBadgeProps {
  status: string;
  label: string;
  warningTooltip?: string;
  infoTooltip?: string;
}

/**
 * Derives the StatusBadge props for a project's status:
 * - Tentative/Archived always get a badge; a scheduled archivation or
 *   unarchivation shows as the badge's warning tooltip.
 * - Active gets a badge only when an archivation is scheduled.
 * - A project that has not started yet gets a "Not started" badge whose info
 *   tooltip tells when it will start.
 * Returns null when there is nothing to flag — a plain active project, or one
 * whose status is still unknown (optimistic entries carry no status yet, so
 * every branch falls through).
 *
 * @param project The assignment's project; undefined when it is not loaded yet.
 * @param t Translation function of the `authenticated.planner` namespace.
 */
export const getProjectStatusBadgeProps = (
  project: AssignmentProject | undefined,
  t: TFunction
): ProjectStatusBadgeProps | null => {
  if (!project) return null;

  const archiveWarning = project.nextArchiveDate
    ? t('common:projectWillBeArchivedOn', {
        date: formatDisplayDate(project.nextArchiveDate.toDate()),
      })
    : undefined;

  if (project.status === ProjectStatus.Tentative) {
    return {
      status: project.status,
      // Show the probability (a 0-100 percent) in the badge when the backend provided it.
      // Round to a whole percent so float scaling artifacts (e.g. 23.4999) don't leak into the UI.
      label:
        project.tentativeProbability != null
          ? t('tooltip.projectStatus.tentativeWithProbability', {
              probability: Math.round(project.tentativeProbability),
            })
          : t('tooltip.projectStatus.tentative'),
      warningTooltip: archiveWarning,
    };
  }
  if (project.status === ProjectStatus.Archived) {
    return {
      status: project.status,
      label: t('tooltip.projectStatus.archived'),
      warningTooltip: project.nextUnarchiveDate
        ? t('common:projectWillBeUnarchivedOn', {
            date: formatDisplayDate(project.nextUnarchiveDate.toDate()),
          })
        : undefined,
    };
  }
  if (project.status === ProjectStatus.Active && archiveWarning) {
    return {
      status: project.status,
      label: t('tooltip.projectStatus.active'),
      warningTooltip: archiveWarning,
    };
  }
  if (!project.status && project.startsAt) {
    return {
      status: ProjectBadgeSyntheticStatus.NotStarted,
      label: t('tooltip.projectStatus.notStarted'),
      infoTooltip: t('tooltip.projectStatus.startsOn', {
        date: formatDisplayDate(project.startsAt.toDate()),
      }),
    };
  }
  return null;
};
